import {
  TextField,
  TextInput,
  SelectInput,
  SelectField,
  BooleanInput,
  FilterList,
  FilterListItem,
  useTheme,
  useRecordContext,
  maxLength,
  FormDataConsumer,
  WithRecord,
  SearchInput,
  SelectArrayInput,
  Toolbar,
} from "react-admin";
import DnsIcon from "@mui/icons-material/Dns";
import { statusChoices, accessModeChoices, statusChoicesDict } from "./base";
import { StatusHeader, TowerOrganization } from "./components";
import { Box, Card, CardContent } from "@mui/material";
import { Link } from "react-router-dom";
import { LocationField, LocationInput } from "../locations/components";
import { MyList } from "../../ra-lb-tools/components/myList";
import { SystemField, SystemInput } from "../systems";
import {
  MyEdit as Edit,
  MyCreate as Create,
  MyEdit,
} from "../../ra-lb-tools/components/myMutator";
import { sxSpot } from "../../ra-lb-tools/theme/styles";
import { getFields } from "../../ra-lb-tools/utils/tools";
import { MyBooleanField as BooleanField } from "../../components/myBooleanField";
import {
  MyReferenceField,
  MyReferenceInput,
} from "../../ra-lb-tools/components/myReference";
import { MyBulkCreate } from "../../ra-lb-tools/components/myBulkCreate";
import { ColoredOptionChipField } from "../../components/coloredOptionChipField";
import { Row } from "../../ra-lb-tools/components/row";
import { MyDateField } from "../../ra-lb-tools/components/myDateField";
import { UserField, UserInput } from "../users";
import { TowerHistorySummaryField } from "../towerHistory";
import { ReferenceInputString } from "../../ra-lb-tools/components/referenceInputString";
import { EditStatusButton } from "./TowerDetail";
import { NullableBooleanField } from "../../ra-lb-tools/components/nullableBooleanField";
import { ReleaseLockersButton, ResetTowerButton } from "./buttons";
import {
  Bluetooth,
  BluetoothDisabled,
  NetworkWifi,
  SignalWifiBad,
} from "@mui/icons-material";
import { DeviceInput } from "../devices";
import {
  ManufacturerField,
  ManufacturerInput,
} from "../tower_model/TowerModel";
import { ConfirmAndSaveButton } from "../../ra-lb-tools/components/confirmAndSaveButton";

export const TowerIcon = DnsIcon;

export const TowerFilterSidebar = () => (
  <Card sx={{ order: -1, mr: 2, width: 200, marginTop: "20px" }}>
    <CardContent>
      <FilterList label="Tower is for Test?">
        <FilterListItem label="Yes" value={{ is_for_test: true }} />
        <FilterListItem label="No" value={{ is_for_test: false }} />
      </FilterList>
    </CardContent>
  </Card>
);

const towerFilters = () => [
  <TextInput source="towerId" alwaysOn key="tower_id" />,
  <TextInput source="name" alwaysOn key="name" />,
  <SearchInput source="search" alwaysOn key="search" no-label />,
  <SelectArrayInput
    source="statusId"
    choices={statusChoices}
    alwaysOn
    key={1}
  />,
  <NullableBooleanField
    source="has_oos"
    label="Is OOS"
    key={"has_oos"}
    alwaysOn
  />,
  <NullableBooleanField
    source="isForTest"
    key={5}
    alwaysOn
    defaultValue={false}
  />,
  <LocationInput source="locationId" allowEmpty alwaysOn key={2} />,
  <ReferenceInputString
    source="last_sync_status"
    reference="event-sync-summary"
    alwaysOn
    key="event-sync-summary"
  />,
  <SystemInput source="systemId" allowEmpty alwaysOn key={3} slack={8} />,
  <UserInput source="ownerId" allowEmpty alwaysOn key={4} />,
  <TextInput source="deviceSerial" key="deviceSerial" />,
  <NullableBooleanField source="isOnline" key={"isOnline"} alwaysOn />,
  <NullableBooleanField
    source="hasOnlineHardware"
    key={"hasOnlineHardware"}
    alwaysOn
  />,
  <ManufacturerInput source="manufacturer" key={"manufacturer"} alwaysOn />,
];

const hasOOS = (record) => record.statusId == 2 || record.oosLockersCount;

const rowStyle = (record, palette) => {
  const backgroundColor = record.isForTest
    ? palette.specialColors.testing
    : hasOOS(record)
    ? palette.specialColors.oos
    : palette.background.paper;

  return { backgroundColor };
};

const OosLockers = () => {
  const record = useRecordContext();
  if (!record) return null;
  if (!record.oosLockersCount) return null;

  const onClick = (e) => e.stopPropagation();
  const to = `/towers/${record.id}/show`;

  return (
    <Link to={to} onClick={onClick} style={sxSpot()}>
      <TextField source="oosLockersCount" />
    </Link>
  );
};

export const OnlineStatusField = ({ tower }) => {
  tower = tower || useRecordContext();
  if (!tower) return null;

  const Icon = tower.hasOnlineHardware
    ? tower.isOnline
      ? NetworkWifi
      : SignalWifiBad
    : tower.isOnline
    ? Bluetooth
    : BluetoothDisabled;
  const color = tower.isOnline ? "success" : "error";

  return <Icon color={color} />;
};

export const TowerList = ({ resource = "towers", omit, ...props }) => {
  const [theme] = useTheme();
  const palette = theme.palette;

  const userFields = (
    <>
      <TextField source="id" />
      <BooleanField source="isForTest" label="Is for test?" />
      <TextField source="name" />
      <SelectField
        source="statusId"
        choices={statusChoices}
        optionText={<ColoredOptionChipField />}
      />
      <OnlineStatusField source="onlineStatus" sortable={false} />
      <Box
        sortable={false}
        source="lastSync"
        label="Last Sync Status"
        sx={{
          ".TowerHistorySummary.status-failed": {
            fontSize: "1.2em !important",
          },
          ".TowerHistoryDetail": {
            display: "none",
          },
        }}
      >
        <WithRecord
          render={(tower) => (
            <TowerHistorySummaryField record={tower.lastSync} />
          )}
        />
      </Box>
      <OosLockers source="oosLockersCount" label="OOS Lockers" />
      <SystemField source="systemId" />
      <LocationField source="locationId" />
      <TextField source="location.city" label="City" />
      <TextField source="location.state" label="State" />
      <SelectField source="accessModeId" choices={accessModeChoices} />
      <UserField source="ownerId" />
      <MyReferenceField
        source="towerModelId"
        reference="tower-models"
        link="show"
      />
      <ManufacturerField source="manufacturer" sortable={false} />
      <TextField source="fwVersion" />
      <TextField source="blVersion" label="Bootloader Version" />
      <BooleanField source="hasKeypad" />
      <TextField source="deviceSerial" />
      <MyDateField source="configuredAt" />
      <TextField source="deviceModel" />
      <TextField source="lastCommand" />
      <TextField source="lastEvent" />
      <MyDateField
        source="lastEventsSync"
        showTime={true}
        label="Last Events Sync"
      />
      <MyDateField source="lastEventsReset" showTime={true} />
      <TextField source="lockersCount" sortable={false} />
      <TextField source="pinCode" />
      <TowerOrganization source="organization" sortable={false} />
      <BooleanField source="fullConfigured" sortable={false} />
      <ResetTowerButton source="resetTower" resource={resource} />
      <ReleaseLockersButton source="releaseLockers" />
    </>
  );

  const debugFields = <></>;

  return (
    <MyList
      filters={towerFilters()}
      resource={resource}
      dataGridProps={{
        rowStyle: (record) => rowStyle(record, palette),
        omit: omit || [
          "deviceSerial",
          "configuredAt",
          "deviceModel",
          "lastCommand",
          "lastEvent",
          "lastEventsSync",
          "lastEventsReset",
          "lockersCount",
          "pinCode",
          "resetTower",
          "releaseLockers",
        ],
      }}
      filterDefaultValues={{
        is_for_test: false,
      }}
      enableBulkCreate={true}
      {...props}
    >
      {getFields(userFields, debugFields)}
    </MyList>
  );
};

const TowerName = () => {
  const nameMaxLength = 16;

  return (
    <Row sx={{ alignItems: "baseline" }}>
      <TextInput
        source="name"
        validate={maxLength(nameMaxLength)}
        inputProps={{ maxLength: nameMaxLength }}
      />
      <Box>
        <FormDataConsumer>
          {({ formData }) => (
            <Box>
              {(formData.name || "").length}/{nameMaxLength}
            </Box>
          )}
        </FormDataConsumer>
      </Box>
    </Row>
  );
};

export const TowerEdit = (props) => {
  return (
    <Edit {...props}>
      <TextInput source="id" disabled />
      <TowerName source="name" />
      <TextInput source="pinCode" />
      <TextInput source="description" multiline />
      <SystemInput label="System" />
      <LocationInput />
      <Row
        source="statusId"
        sx={{
          ".EditStatusButton": {
            height: "56px",
            marginLeft: "12px",
            ".MuiButton-startIcon": { margin: "0" },
          },
        }}
      >
        <SelectInput
          source="statusId"
          choices={statusChoices}
          optionValue="id"
          optionText="name"
          label={<StatusHeader />}
          disabled
        />
        <EditStatusButton noLabel />
      </Row>
      <SelectInput
        source="accessModeId"
        choices={accessModeChoices}
        optionValue="id"
        optionText="name"
      />
      <UserInput
        label="Owner"
        source="ownerId"
        filter={{ can_own_towers: true }}
        allowEmpty
      />
      <TextInput source="privateNotes" multiline />
      <BooleanInput source="isForTest" />
      <BooleanInput source="hasKeypad" />
      <BooleanInput source="isOnline" disabled={true} />
      <DeviceInput source="deviceSerial" target="serialNumber" />
    </Edit>
  );
};

export const TowerCreate = (props) => {
  return (
    <Create {...props}>
      <TowerName source="name" />
      <TextInput source="pinCode" />
      <TextInput source="description" multiline />
      <SystemInput source="systemId" />
      <LocationInput />
      <MyReferenceInput source="towerModelId" reference="tower-models" />
      <SelectInput
        source="accessModeId"
        choices={accessModeChoices}
        optionValue="id"
        optionText="name"
      />
      <UserInput
        source="ownerId"
        filter={{ can_own_towers: true }}
        allowEmpty
      />
      <SelectInput
        source="statusId"
        choices={statusChoices}
        optionValue="id"
        optionText="name"
        label={<StatusHeader />}
      />
      <TextInput source="privateNotes" multiline />
      <BooleanInput source="isForTest" />
      <BooleanInput source="hasKeypad" />
    </Create>
  );
};

export const TowerBulkCreate = () => {
  const schema = [
    {
      name: "string",
      systemId: 0,
      towerModelId: 0,
      locationId: 0,
      description: "string",
      pinCode: "string",
      accessModeId: 1,
      isForTest: false,
      hasKeypad: false,
      privateNotes: "string",
      statusId: 1,
      ownerId: "string",
    },
  ];

  return (
    <MyBulkCreate
      resource="towers"
      label="JSON Tower List"
      schema={schema}
      requiredFields={4}
    />
  );
};

const render = (record, DefaultLabel) => (
  <>
    {record.isForTest ? "TEST -- " : ""}
    {DefaultLabel}
    {record.statusId == 4 ? " -- RETIRED" : ""}
  </>
);

export const TowerInput = (props) => {
  return (
    <MyReferenceInput
      source="towerId"
      reference="towers"
      render={render}
      {...props}
    />
  );
};

export const TowerField = (props) => {
  return (
    <MyReferenceField
      source="towerId"
      reference="towers"
      render={render}
      queryOptions={{ meta: { status_id: 0 } }}
      {...props}
    />
  );
};

export const TowerIsOnlineEdit = (props) => {
  return (
    <MyEdit
      resource="towers"
      {...props}
      formProps={{
        shouldUnregister: true,
        toolbar: (
          <Toolbar>
            <ConfirmAndSaveButton
              showDialog={(record, valuesToSave) => {
                const prevOnline = record?.isOnline;
                const newOnline = valuesToSave.isOnline;

                return prevOnline && !newOnline;
              }}
              confirmMessage={
                <Box>
                  <Box>Marking tower as Offline.</Box>
                  <Box>Are you sure?</Box>
                </Box>
              }
            />
          </Toolbar>
        ),
      }}
      mutationOptions={{
        meta: { customSegment: "online" },
      }}
      permissionAction="update_online"
    >
      <TextInput source="id" disabled />
      <TextInput source="name" disabled />
      <BooleanInput source="isOnline" />
    </MyEdit>
  );
};

export const TowerTowerModelEdit = (props) => {
  return (
    <MyEdit
      resource="towers"
      {...props}
      formProps={{
        shouldUnregister: true,
      }}
      mutationOptions={{
        meta: { customSegment: "model" },
      }}
      permissionAction="update_tower_model"
    >
      <TextInput source="id" disabled />
      <TextInput source="name" disabled />
      <WithRecord
        render={(record) => (
          <MyReferenceInput
            source="towerModelId"
            reference="tower-models"
            disabled={record.lockersCount}
          />
        )}
      />
    </MyEdit>
  );
};

export const TowerStatusEdit = (props) => {
  return (
    <MyEdit
      resource="towers"
      {...props}
      formProps={{
        shouldUnregister: true,
        toolbar: (
          <Toolbar>
            <ConfirmAndSaveButton
              showDialog={(record, valuesToSave) => {
                const prevStatus = record?.statusId;
                const newStatus = valuesToSave.statusId;

                return (
                  prevStatus == statusChoicesDict.Available.id &&
                  newStatus == statusChoicesDict.Pending.id
                );
              }}
              confirmMessage={
                <Box>
                  <Box>Moving tower from Available to Pending</Box>
                  <Box>Are you sure this board is reset on site?</Box>
                </Box>
              }
            />
          </Toolbar>
        ),
      }}
      mutationOptions={{
        meta: { customSegment: "status" },
      }}
      permissionAction="update_status"
    >
      <TextInput source="id" disabled />
      <TextInput source="name" disabled />
      <SelectInput
        source="statusId"
        choices={statusChoices}
        optionValue="id"
        optionText="name"
        label={<StatusHeader />}
      />
    </MyEdit>
  );
};
